import { css } from '@emotion/css'
import { faArrowAltCircleRight, faCalendar } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useViewportSize } from '@mantine/hooks'
import { Button } from 'components/Button/Button'
import type CssType from 'csstype'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import type { CSSProperties } from 'react'
import React, { useMemo } from 'react'
import { colors } from 'settings/colors'
import { constructDateWithTimeZone } from './_utils/constructDateWithTimeZone'
import { getDateRangeLimits } from './_utils/getDateRangeLimits'
import { pickerControlClassName } from './_utils/pickerControlClassName'
import { MantineDateRangePickerWithFooter } from './MantineDateRangePickerWithFooter/MantineDateRangePickerWithFooter'
import type { DatesRangeValue, DateValue } from './MantineDateRangePickerWithFooter/types'

export const DateRangePicker = (props: {
  handleApply: (props: {
    handleValueChange: (range: DatesRangeValue) => void
    setDropdownOpened: React.Dispatch<React.SetStateAction<boolean>>
    value: DatesRangeValue | null
  }) => void
  dateFrom?: moment.Moment
  dateTo?: moment.Moment
  width?: CssType.Property.Width<string | number>
  hasResponsiveWidth?: boolean
  height: number
  minDate?: moment.Moment
  maxDate?: moment.Moment
  // Historical-only surfaces (e.g. dashboards): clamp the max date to today and drop the future-oriented
  // presets ("Next N Days"), so "Last N Days" presets end today instead of running into the forecast.
  isHistoricalOnly?: boolean
  // Regional-only users: a year of history and at most a week of forecast.
  isSelfRegisteredUser?: boolean
  timezone: string
}) => {
  const timezone = moment.tz.guess()
  // TODO: use the new hook once merged in
  const { width: screenWidth } = useViewportSize()
  const dateFromString = props.dateFrom?.toString()
  const dateToString = props.dateTo?.toString()

  const defaultValue = useMemo<DatesRangeValue>(
    () => [
      dateFromString ? constructDateWithTimeZone(dateFromString, timezone) : null,
      dateToString ? constructDateWithTimeZone(dateToString, timezone) : null,
    ],
    [dateFromString, dateToString, timezone],
  )

  const { forecastDays, maxDate, minDate, presetEndDate } = getDateRangeLimits({
    isHistoricalOnly: props.isHistoricalOnly,
    isSelfRegisteredUser: props.isSelfRegisteredUser,
    maxDate: props.maxDate,
    minDate: props.minDate,
    timezone,
  })

  return (
    <MantineDateRangePickerWithFooter
      clearable={false}
      closeOnChange={false}
      defaultValue={defaultValue}
      leftSection={<FontAwesomeIcon css={{ color: colors.grey800, fontSize: 16 }} icon={faCalendar} />}
      minDate={minDate}
      maxDate={maxDate}
      placeholder={translate.phrases.banyanApp('Select Date Range')}
      timezone={timezone}
      valueFormat={translate.dates.getMomentFormat('MMM D, YYYY')}
      renderDayWithValue={(day: DateValue, value: DatesRangeValue) => {
        const isToday = moment.tz(timezone).isSame(day, 'day')
        const isInRange = value[0] && value[1] && moment.tz(day, timezone).isBetween(value[0], value[1])

        const isSelected =
          (value[0] && !value[1] && moment.tz(day, timezone).isSame(value[0], 'day')) ||
          (value[1] && !value[0] && moment.tz(day, timezone).isSame(value[1], 'day'))

        const isFirstInRange = value[0] && value[1] && moment.tz(day, timezone).isSame(value[0], 'day')
        const isLastInRange = value[0] && value[1] && moment.tz(day, timezone).isSame(value[1], 'day')

        let style: CSSProperties = {
          borderRadius: '0',
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }

        if (isToday) {
          style = {
            ...style,
            border: `2px solid ${colors.primary}`,
            borderRadius: isInRange || isSelected ? '0' : '3px',
            color: colors.primary,
            fontWeight: 'bold',
          }
        }

        if (isSelected || isFirstInRange || isLastInRange) {
          let selectionStyle: CSSProperties = {
            backgroundColor: colors.midnight,
            color: 'white',
            ...(isToday ? { fontWeight: 'normal' } : {}),
          }

          style = {
            ...style,
            ...selectionStyle,
            borderRadius: '3px',
          }
        } else if (isInRange) {
          style = {
            ...style,
            backgroundColor: colors.grey500,
          }
        }

        return <div style={style}>{translate.dates.format(moment.tz(day, timezone), 'D')}</div>
      }}
      Footer={(footerProps: {
        handleValueChange: (range: DatesRangeValue) => void
        setDropdownOpened: React.Dispatch<React.SetStateAction<boolean>>
        value: DatesRangeValue | null
      }) => (
        <div
          style={{
            display: 'flex',
            gap: 6,
            maxWidth: 266, // TODO: magic number from mantine
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 10,
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <div
            className={css`
              display: flex;
              width: 100%;
              flex-wrap: wrap;
              justify-content: space-around;
            `}
          >
            <Button
              className={css`
                width: 48%;
              `}
              onClick={() =>
                footerProps.handleValueChange([
                  constructDateWithTimeZone(
                    moment.tz(timezone).subtract(7, 'days').startOf('day').toString(),
                    timezone,
                  ),
                  presetEndDate,
                ])
              }
              variant="tertiary"
            >
              {translate.phrases.banyanApp('Last {{count}} Days', { count: 7 })}
            </Button>
            <Button
              className={css`
                width: 48%;
              `}
              onClick={() =>
                footerProps.handleValueChange([
                  constructDateWithTimeZone(
                    moment.tz(timezone).subtract(14, 'days').startOf('day').toString(),
                    timezone,
                  ),
                  presetEndDate,
                ])
              }
              variant="tertiary"
            >
              {translate.phrases.banyanApp('Last {{count}} Days', { count: 14 })}
            </Button>
          </div>
          {/* Future-oriented + "Last 30" presets only outside historical-only mode (keeps a clean single
              row of "Last 7 / Last 14" for dashboards). */}
          {!props.isHistoricalOnly && (
            <div
              className={css`
                display: flex;
                width: 100%;
                flex-wrap: wrap;
                justify-content: space-around;
              `}
            >
              <Button
                className={css`
                  width: 48%;
                `}
                onClick={() =>
                  footerProps.handleValueChange([
                    constructDateWithTimeZone(
                      moment.tz(timezone).subtract(30, 'days').startOf('day').toString(),
                      timezone,
                    ),
                    presetEndDate,
                  ])
                }
                variant="tertiary"
              >
                {translate.phrases.banyanApp('Last {{count}} Days', { count: 30 })}
              </Button>
              <Button
                className={css`
                  width: 48%;
                `}
                onClick={() =>
                  footerProps.handleValueChange([
                    constructDateWithTimeZone(moment.tz(timezone).startOf('day').toString(), timezone),
                    presetEndDate,
                  ])
                }
                variant="tertiary"
              >
                {translate.phrases.banyanApp('Next {{count}} Days', { count: forecastDays })}
              </Button>
            </div>
          )}
          <Button
            fullWidth={true}
            disabled={!(footerProps.value && footerProps.value.every((v) => v !== null))}
            onClick={() => {
              if (footerProps.value && footerProps.value.every((v: DateValue) => v !== null)) {
                footerProps.setDropdownOpened(false)

                props.handleApply(footerProps)
              }
            }}
            rightIcon={<FontAwesomeIcon icon={faArrowAltCircleRight} />}
          >
            {translate.phrases.banyanApp('Apply')}
          </Button>
        </div>
      )}
      styles={() => {
        return {
          /**
           * narrower screens can see a date picker that doesn't fit quite right.
           * "month" is the table element, and tables can't get maxWidth, but a
           * width of 100% is pretty ugly
           */
          month: (screenWidth || window.innerWidth) < 369 ? { width: '100%' } : {},
          section: {
            // by default, mantine sticks it on the left, but our designs like the right side
            left: 'auto',
            right: 0,
          },
          input: {
            border: `1px solid ${colors.grey800}`,
            padding: '0px 12px',
            height: props.height,
          },
          day: {
            marginTop: 2,
            marginBottom: 2,
            marginRight: -1,
          },
          root: {
            width: props.width,
            height: 30,
            marginBottom: 18,
          },
        }
      }}
      classNames={{ monthsListControl: pickerControlClassName, yearsListControl: pickerControlClassName }}
      hasResponsiveWidth={props.hasResponsiveWidth}
    />
  )
}
